import { AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { AppToken } from "App";
import { ReactNode, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { logout } from "../utils/apiHelper";

const IDLE_TIMEOUT_MS = 1800000;
const RESET_THROTTLE_MS = 5000;

export function SessionProvider({
  children,
  account,
}: {
  children: ReactNode;
  account: AccountInfo | null;
}) {
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef(0);
  const { instance } = useMsal();
  const { authorizedToken } = JSON.parse(useContext(AppToken));
  const logoutUrl = account?.tenantId
    ? `https://login.microsoftonline.com/${account.tenantId}`
    : undefined;

  useEffect(() => {
    if (exemptedRoutes.includes(location.pathname)) return;
    if (!authorizedToken || !logoutUrl) return;

    const armTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      lastResetRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        logout(authorizedToken)
          .catch((error) => {
            console.error("Backend logout failed", error);
          })
          .finally(() => {
            instance.logoutRedirect({
              account: account?.homeAccountId
                ? (instance.getAccountByHomeId(account.homeAccountId) ??
                  undefined)
                : undefined,
              authority: logoutUrl,
              postLogoutRedirectUri: `${window.location.origin}${process.env.PUBLIC_URL ?? ""}`,
            });
          });
      }, IDLE_TIMEOUT_MS);
    };

    const handleWindowEvents = () => {
      const now = Date.now();
      if (now - lastResetRef.current < RESET_THROTTLE_MS) {
        return;
      }
      armTimeout();
    };

    window.addEventListener("mousemove", handleWindowEvents);
    window.addEventListener("keydown", handleWindowEvents);
    window.addEventListener("click", handleWindowEvents);
    window.addEventListener("scroll", handleWindowEvents);

    armTimeout();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener("mousemove", handleWindowEvents);
      window.removeEventListener("keydown", handleWindowEvents);
      window.removeEventListener("click", handleWindowEvents);
      window.removeEventListener("scroll", handleWindowEvents);
    };
  }, [
    account?.homeAccountId,
    authorizedToken,
    instance,
    location.pathname,
    logoutUrl,
  ]);

  return <>{children}</>;
}

const exemptedRoutes = ["/signIn"];
